import * as Sentry from "@sentry/react";

type LogData = Record<string, unknown>;

const isDev = import.meta.env.DEV;

// Check if Sentry.logger is available (requires _experiments.enableLogs and SDK support)
const hasSentryLogger = typeof Sentry.logger?.warn === 'function';

if (isDev && !hasSentryLogger) {
  console.warn("[LOGGER] Sentry.logger not available - logs will only appear as breadcrumbs");
}

/**
 * Lightweight logger for frontend error tracking.
 *
 * Philosophy:
 *   - Frontend logs ERRORS only (things that went wrong)
 *   - Backend handles operational logging (API requests, timing, etc.)
 *   - Breadcrumbs auto-capture user actions leading to errors
 *
 * Fallback behavior:
 *   - If Sentry.logger is unavailable, logs are added as breadcrumbs
 *   - Breadcrumbs appear in the context of the next error/exception
 */
class Logger {

  debug(message: string, data?: LogData) {
    if (isDev) {
      console.log("[DEBUG]", message, data || "");
    }
  }

  info(message: string, data?: LogData) {
    if (isDev) {
      console.info("[INFO]", message, data || "");
    }
    // Add as breadcrumb so it appears in error context
    Sentry.addBreadcrumb({
      category: "log",
      message,
      level: "info",
      data,
    });
  }

  /**
   * Warning - sent to Sentry Logs (with breadcrumb fallback)
   */
  warn(message: string, data?: LogData) {
    if (isDev) {
      console.warn("[WARN]", message, data || "");
    }

    // Try Sentry.logger first, fall back to breadcrumb
    if (hasSentryLogger) {
      Sentry.logger.warn(message, data);
    } else {
      Sentry.addBreadcrumb({
        category: "log",
        message,
        level: "warning",
        data,
      });
    }
  }

  /**
   * Error - sent to Sentry Logs (with breadcrumb fallback)
   */
  error(message: string, data?: LogData) {
    if (isDev) {
      console.error("[ERROR]", message, data || "");
    }

    // Try Sentry.logger first, fall back to breadcrumb
    if (hasSentryLogger) {
      Sentry.logger.error(message, data);
    } else {
      Sentry.addBreadcrumb({
        category: "log",
        message,
        level: "error",
        data,
      });
    }
  }

  /**
   * Capture an actual Error object with full stack trace.
   * Use this for caught exceptions - gives you the best debugging info.
   */
  captureException(error: Error, context?: LogData) {
    if (isDev) {
      console.error(`[EXCEPTION] ${error.message}`, context || "");
    }
    Sentry.captureException(error, {
      extra: context,
    });
  }

  /**
   * Set user context for all subsequent errors.
   * This attaches user info to ALL Sentry events (errors, logs, performance).
   */
  setUser(user: { id?: string; email?: string; username?: string }) {
    if (isDev) {
      console.log("[SENTRY] User context set:", user);
    }
    Sentry.setUser(user);
  }

  /**
   * Track performance of an async operation.
   * Sends timing data to Sentry Performance monitoring.
   * Logs warning if operation exceeds threshold.
   *
   * Usage:
   *   const result = await logger.trackOperation("Export entries", async () => {
   *     return await exportEntries(ids);
   *   }, { entryCount: ids.length });
   */
  async trackOperation<T>(
    name: string,
    operation: () => Promise<T>,
    context?: LogData,
    warnThresholdMs = 5000
  ): Promise<T> {
    const startTime = performance.now();

    return Sentry.startSpan({ name, op: "ui.action" }, async () => {
      try {
        const result = await operation();
        const durationMs = Math.round(performance.now() - startTime);

        if (durationMs > warnThresholdMs) {
          this.warn(`${name} took ${durationMs}ms`, { ...context, durationMs });
        } else if (isDev) {
          console.log(`[PERF] ${name}: ${durationMs}ms`, context || "");
        }

        return result;
      } catch (error) {
        const durationMs = Math.round(performance.now() - startTime);
        this.error(`${name} failed after ${durationMs}ms`, {
          ...context,
          durationMs,
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    });
  }
}

export const logger = new Logger();
